import { useState, useEffect, useRef } from 'react';
import { 
  graphPython,
  graphJava,
  graphCpp,
  graphC
} from './graphAlgorithms';
import defaultExplanations from './explanations';
import codeSamplesTemplate from './codeSamples';
import { useSpeechSynthesis } from '../speechUtils';

export const useGraph = (speakingLang, translatedExplanations, language, graphInput, edgeDirection, graphInputForC, maxVertices, breakpoints) => {
  const [explanations, setExplanations] = useState(defaultExplanations);
  const [highlightedLine, setHighlightedLine] = useState(null);
  const [highlightedMultipleLines, setHighlightedMultipleLines] = useState([]);
  const [focusedEndLine, setFocusedEndLine] = useState(null);
  const [callingLine, setCallingLine] = useState(null);
  const [secondCallingLine, setSecondCallingLine] = useState(null);
  const [thirdCallingLine, setThirdCallingLine] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);
  const [delay, setDelay] = useState(2000);
  const delayRef = useRef(delay);
  const breakpointsRef = useRef(breakpoints);
  const [logs, setLogs] = useState([]);
  const [variables, setVariables] = useState({});
  const [highlightedVariables, setHighlightedVariables] = useState([]);
  const [graphVariables, setGraphVariables] = useState({});
  const [highlightedGraphVariables, setHighlightedGraphVariables] = useState([]);
  const [mapVariables, setmapVariables] = useState({});
  const [highlightedMapVariables, setHighlightedMapVariables] = useState([]);
  const [highlightedMapIndex, setHighlightedMapIndex] = useState([]);
  const [mapVariablesForC, setMapVariablesForC] = useState({});
  const [highlightedMapVariablesForC, setHighlightedMapVariablesForC] = useState([]);
  const [highlightedMapIndexForC, setHighlightedMapIndexForC] = useState([]);
  const [highlightedLineArrow, setHighlightedLineArrow] = useState([]);
  const [nodeVariables, setNodeVariables] = useState({});
  const [highlightedNodeVariables, setHighlightedNodeVariables] = useState([]);
  const [highlightNodeAddress, setHighlightNodeAddress] = useState([]);
  const [highlightDataPart, setHighlightDataPart] = useState([]);
  const [highlightNextAddressPart, setHighlightNextAddressPart] = useState([]);
  const [adjList, setAdjList] = useState([]);
  const [codeSamples, setCodeSamples] = useState(codeSamplesTemplate[language]);
  const { speak, setIsMuted } = useSpeechSynthesis(isPausedRef, speakingLang);
  const [isMuted, setLocalIsMuted] = useState(false);
  const isMutedRef = useRef(isMuted);
  

  useEffect(() => {
    setIsMuted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    breakpointsRef.current = breakpoints;
  }, [breakpoints]);

  useEffect(() => {
    if (translatedExplanations && language) {
      setExplanations((prevExplanations) => ({
        ...prevExplanations,
        [language]: Object.keys(prevExplanations[language] || {}).reduce((acc, key, index) => {
          acc[key] = translatedExplanations[index] || prevExplanations[language][key];
          return acc;
        }, {}),
      }));
    } else {
      setExplanations(defaultExplanations);
    }
  }, [translatedExplanations, language]);

  const updateVariables = (newGraphData, edgeDirected) => {
    resetState();
    
    // Dynamically update the code samples
    let updatedCodeSamples = "";
    let index = 0;
  
    if (language === "Python") {
      updatedCodeSamples = codeSamplesTemplate[language].map((line) => {
        return line.replace(/add_edge\(\s*\d+,\s*\d+(,\s*directed=\w+)?\s*\)/g, () => {
          if (index < newGraphData.length) {
            const [from, to] = newGraphData[index];
            const directed = edgeDirected[index];
            index++;
            return `add_edge(${from}, ${to}${
              directed !== undefined ? `, directed=${directed ? 'True' : 'False'}` : ""
            })`;
          }
          return ""; // Return empty string if out of range
        });
      });
    } else if (language === "C") {
      updatedCodeSamples = codeSamplesTemplate[language].map((line) => {
        return line.replace(/addEdge\(graph,\s*\d+,\s*\d+,\s*(true|false)\s*\);/g, () => {
          if (index < newGraphData.length) {
            const [from, to] = newGraphData[index];
            const directed = edgeDirected[index];
            index++;
            return `addEdge(graph, ${from}, ${to}, ${directed});`;
          }
          return ""; // Return an empty string if out of range
        });
      });
    } else {
      updatedCodeSamples = codeSamplesTemplate[language].map((line) => {
        return line.replace(/addEdge\(\s*\d+,\s*\d+,\s*(true|false)\s*\);/g, () => {
          if (index < newGraphData.length) {
            const [from, to] = newGraphData[index];
            const directed = edgeDirected[index];
            index++;
            return `addEdge(${from}, ${to}, ${directed});`;
          }
          return ""; // Return an empty string if out of range
        });
      });
    }
  
    // Update the state
    setCodeSamples(updatedCodeSamples);
  };

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    delayRef.current = delay;
  }, [delay]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const resetState = () => {
    setHighlightedLine(null);
    setHighlightedMultipleLines([]);
    setFocusedEndLine(null);
    setCallingLine(null);
    setSecondCallingLine(null);
    setThirdCallingLine(null);
    setGraphVariables({});
    setLogs([]);
    setHighlightedGraphVariables([]);
    setVariables({});
    setHighlightedVariables([]);
    setmapVariables({});
    setHighlightedMapVariables([]);
    setHighlightedMapIndex([]);
    setMapVariablesForC({});
    setHighlightedMapVariablesForC([]);
    setHighlightedMapIndexForC([]);
    setHighlightedLineArrow([]);
    setNodeVariables({});
    setHighlightedNodeVariables([]);
    setHighlightNodeAddress([]);
    setHighlightDataPart([]);
    setHighlightNextAddressPart([]);
    setAdjList([]);
  };

  const logMessage = async (message) => {
    setLogs((prevLogs) => [...prevLogs, message]);
    await speak(message);
  };

  const highlightLine = async (lineNumber, customLog=null) => {
    setCallingLine((prevCallingLine) => {
      if (prevCallingLine === lineNumber) {
        return null;
      }
      return prevCallingLine;
    });

    setSecondCallingLine((prevCallingLine) => {
      if (prevCallingLine === lineNumber) {
        return null;
      }
      return prevCallingLine;
    });

    setThirdCallingLine((prevCallingLine) => {
      if (prevCallingLine === lineNumber) {
        return null;
      }
      return prevCallingLine;
    });

    setHighlightedLine(lineNumber);

    const logMessageText = customLog 
    ? `Line ${lineNumber + 1}: ${customLog}` 
    : `Line ${lineNumber + 1}: ${explanations[language][lineNumber + 1] || "Executing line " + (lineNumber + 1)}`;

    await logMessage(logMessageText);
  };

  const highlightCallingLine = async (lineNumber) => {
    setCallingLine(lineNumber);
  };

  const highlightSecondCallingLine = async (lineNumber) => {
    setSecondCallingLine(lineNumber);
  };

  const highlightThirdCallingLine = async (lineNumber) => {
    setThirdCallingLine(lineNumber);
  };

  const highlightMultipleLines = async (lineNumbers) => {
    setHighlightedMultipleLines(lineNumbers);
    let lineNumber = lineNumbers[0];
    
    if (lineNumber) {
      await logMessage(`Line ${lineNumber + 1}: ${explanations['Python'][lineNumber + 1] || "Executing line " + (lineNumber + 1)}`);
    }
  };

  const focusEndLine = async (lineNumber) => {
    setFocusedEndLine(lineNumber);
    let message = "End of the execution";
    await speak(message);
  };

  const customSleep = async (lineNumber = null, time=null) => {
    // Check if the current line matches a breakpoint
    if (lineNumber !== null && breakpointsRef.current.includes(lineNumber)) {
      setIsPaused(true); // Pause execution
      isPausedRef.current = true;

      // Wait until `isPaused` is set to false
      await new Promise((resolve) => {
          const interval = setInterval(() => {
              if (!isPausedRef.current) {
                  clearInterval(interval);
                  resolve();
              }
          }, 100);
      });

      return;
    }

    // Handle regular sleep with pause checks
    let start = Date.now();
    let ms;
    if (time === null) {
        ms = delayRef.current;
    } else {
        ms = time;
    }
    
    while (Date.now() - start < ms) {
      if (isPausedRef.current) {
        await new Promise((resolve) => {
          let interval = setInterval(() => {
            if (!isPausedRef.current) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        });
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  };

  const runGraphPython = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    await graphPython(
      graphInput,
      edgeDirection,
      setGraphVariables,
      setHighlightedGraphVariables,
      setVariables,
      setHighlightedVariables,
      setmapVariables,
      setHighlightedMapVariables,
      setHighlightedMapIndex,
      setHighlightedLineArrow,
      highlightLine,
      highlightCallingLine,
      customSleep
    );
    
    await focusEndLine(21);
    setIsRunning(false);
  };

  const runGraphJava = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    await graphJava(
      graphInput,
      edgeDirection,
      setGraphVariables,
      setHighlightedGraphVariables,
      setVariables,
      setHighlightedVariables,
      setmapVariables,
      setHighlightedMapVariables,
      setHighlightedMapIndex,
      setHighlightedLineArrow,
      highlightLine,
      highlightCallingLine,
      customSleep
    );

    await focusEndLine(28);
    setIsRunning(false);
  };

  const runGraphCpp = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    await graphCpp(
      graphInput,
      edgeDirection,
      setGraphVariables,
      setHighlightedGraphVariables,
      setVariables,
      setHighlightedVariables,
      setmapVariables,
      setHighlightedMapVariables,
      setHighlightedMapIndex,
      setHighlightedLineArrow,
      highlightLine,
      highlightCallingLine,
      customSleep
    );

    await highlightLine(35);
    await customSleep(35);

    await focusEndLine(35);
    setIsRunning(false);
  };

  const runGraphC = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    await graphC(
      maxVertices,
      graphInputForC,
      edgeDirection,
      setGraphVariables,
      setHighlightedGraphVariables,
      setVariables,
      setHighlightedVariables,
      setAdjList,
      setMapVariablesForC,
      setHighlightedMapVariablesForC,
      setHighlightedMapIndexForC,
      setHighlightedLineArrow,
      highlightLine,
      highlightMultipleLines,
      highlightCallingLine,
      highlightSecondCallingLine,
      highlightThirdCallingLine,
      setNodeVariables,
      setHighlightedNodeVariables,
      setHighlightNodeAddress,
      setHighlightDataPart,
      setHighlightNextAddressPart,
      customSleep
    );

    await highlightLine(52);
    await customSleep(52);

    await focusEndLine(53);
    setIsRunning(false);
  };

  const runGraph = async () => {
    if (language === 'Python') {
      await runGraphPython();
    } else if (language === 'Java') {
      await runGraphJava();
    } else if (language === 'C') {
      await runGraphC();
    } else if (language === 'C++') {
      await runGraphCpp();
    }
  };

  return {
    highlightedLine,
    highlightedMultipleLines,
    focusedEndLine,
    callingLine,
    secondCallingLine,
    thirdCallingLine,
    isRunning,
    isPaused,
    delay,
    logs,
    graphVariables,
    highlightedGraphVariables,
    variables,
    highlightedVariables,
    adjList,
    mapVariables,
    highlightedMapVariables,
    highlightedMapIndex,
    mapVariablesForC,
    highlightedMapVariablesForC,
    highlightedMapIndexForC,
    highlightedLineArrow,
    nodeVariables,
    highlightedNodeVariables,
    highlightNodeAddress,
    highlightDataPart,
    highlightNextAddressPart,
    isMuted,
    codeSamples,
    speak,
    setIsMuted: setLocalIsMuted,
    updateVariables,
    setHighlightedLine,
    setHighlightedMultipleLines,
    setFocusedEndLine,
    setCallingLine,
    setSecondCallingLine,
    setThirdCallingLine,
    setIsRunning,
    setIsPaused,
    setDelay,
    setLogs,
    setGraphVariables,
    setHighlightedGraphVariables,
    runGraph
  };
};
